import { isEqual, partition } from "lodash-es"
import { GRANULARITY_SECOND, compareByDate, localeCompare, log, toArray } from "@nx-superprep/utils"
import { DocumentData, QueryDocumentSnapshot, Timestamp } from "firebase/firestore"
import { DayPlanModule as BackendDayPlanModule } from "@longtail-education/superprepstudy-data-model-frontend"
import { AppAvailability } from "./app-availability"
import { Constants } from "."

/**
 * Represents a day plan module.
 * Extends the BackendDayPlanModule class from the frontend data model.
 */
export class DayPlanModule extends BackendDayPlanModule {
  /** The ID of the day plan module. */
  declare id: string

  appAvailability: AppAvailability = new AppAvailability()

  /**
   * Constructs a new DayPlanModule object.
   * @param documentData The data object containing day plan module information.
   */
  constructor(documentData: DocumentData = {}) {
    super(
      documentData['userId'] ?? '',
      documentData['date'] ?? Timestamp.now(),
      documentData['moduleId'] ?? '',
      documentData['moduleTitle'] ?? '',
      documentData['moduleIcon'],
      documentData['includedDate'] ?? Timestamp.now(),
    )
    this.id = documentData['id']
    this.progress = Number(documentData['progress']) || 0
    this.highestCorrectStreak = Number(documentData['highestCorrectStreak']) || 0
    this.estimatedCompletionTime = Number(documentData['estimatedCompletionTime']) || 0
    this.actualStudyTime = Number(documentData['actualStudyTime']) || 0
    this.isFinished = documentData['isFinished'] || this.progress >= 1
    this.isReviewFinished = documentData['isReviewFinished']
    this.isAcquisitionFinished = documentData['isAcquisitionFinished']
    this.acquisitionTarget = Number(documentData['acquisitionTarget']) || 0
    this.acquisitionProgress = Number(documentData['acquisitionProgress']) || 0
    this.reviewTarget = Number(documentData['reviewTarget']) || 0
    this.reviewProgress = Number(documentData['reviewProgress']) || 0
    this.reviewCardIds = documentData['reviewCardIds'] ?? []
    this.expectedStudyDate = documentData['expectedStudyDate']
    this.includedDate = documentData['includedDate']
    this.lastStudyDate = documentData['lastStudyDate']
    this.isAssigned = documentData['isAssigned']

    // Validate and update the state
    this.validateAndUpdateState()
  }

  /**
   * Validates and updates the state of the DayPlanModule to ensure coherence.
   */
  private validateAndUpdateState(): void {
    // Remove duplicates from reviewCardIds and update reviewTarget
    this.fixReviewCardIdsDuplicates()

    // Clamp acquisitionProgress and reviewProgress to their respective targets
    this.acquisitionProgress = Math.min(this.acquisitionProgress, this.acquisitionTarget)
    this.reviewProgress = Math.min(this.reviewProgress, this.reviewTarget)

    // Set isAcquisitionFinished and isReviewFinished based on progress
    this.isAcquisitionFinished = this.acquisitionProgress === this.acquisitionTarget
    this.isReviewFinished = this.reviewProgress === this.reviewTarget

    // Set progress to 1 if both acquisition and review are complete
    if (this.isAcquisitionFinished && this.isReviewFinished) {
      this.progress = 1
    }

    // Set isFinished based on progress or the provided value
    this.isFinished = this.isFinished || this.progress >= 1
  }

  /**
   * Detects and removes duplicates in reviewCardIds and adjusts reviewTarget accordingly.
   */
  private fixReviewCardIdsDuplicates(): void {
    if (!this.reviewCardIds || !Array.isArray(this.reviewCardIds)) {
      this.reviewCardIds = []
      this.reviewTarget = 0
      return
    }

    // Get the original length
    const originalLength = this.reviewCardIds.length

    // Create a new array with unique card IDs
    const uniqueCardIds = [...new Set(this.reviewCardIds)]

    // Update reviewCardIds with unique values
    this.reviewCardIds = uniqueCardIds

    // If duplicates were found, adjust reviewTarget
    if (uniqueCardIds.length !== originalLength) {
      log.warn(`duplicates found in ${this.moduleId}`)
      // Adjust reviewTarget to match the number of unique cards
      if (this.reviewTarget === originalLength) {
        // Only adjust if reviewTarget matches the original count
        this.reviewTarget = uniqueCardIds.length
      } else if (this.reviewTarget > uniqueCardIds.length) {
        // If reviewTarget is greater than unique cards count, reduce it
        this.reviewTarget = uniqueCardIds.length
      }
      // Note: We don't increase reviewTarget if it was manually set lower
    }
  }

  /**
   * Firestore data converter for DayPlanModule.
   */
  static override converter = {
    toFirestore: (data: DayPlanModule): DocumentData => {
      // Validate and update the state before converting to Firestore
      data.validateAndUpdateState()
      // if dev/prod data model differ
      // if (environment.config.isProduction) { delete data['isAssigned'] }
      // Pass the validated data to the parent class's converter
      return BackendDayPlanModule.converter.toFirestore(data)
    },
    fromFirestore(snapshot: QueryDocumentSnapshot): DayPlanModule {
      const obj = BackendDayPlanModule.converter.fromFirestore(snapshot)
      return new DayPlanModule(obj)
    }
  }

  /**
   * Checks if two DayPlanModule objects are equal.
   * @param a The first DayPlanModule object.
   * @param b The second DayPlanModule object.
   * @returns True if the objects are equal, false otherwise.
   */
  static isEqual(a: DayPlanModule | DayPlanModule[] | undefined, b: DayPlanModule | DayPlanModule[] | undefined): boolean {
    return isEqual(a, b)
  }

  /**
   * Checks if the provided DayPlanModule object is newer than another DayPlanModule object.
   * @param a The first DayPlanModule object.
   * @param b The second DayPlanModule object.
   * @returns True if 'a' is newer than 'b', false otherwise.
   */
  static isNewer(a: DayPlanModule | DayPlanModule[] | undefined, b: DayPlanModule | DayPlanModule[] | undefined): boolean {
    const as = toArray(a)
    const bs = toArray(b)
    return as.length > 0 &&
      (bs.length === 0 || bs.every(_b => {
        const found = as.find(_a => _a.moduleId === _b.moduleId)
        return !!found && DayPlanModule.compare(found, _b) > 0
      }))
  }

  static compareByIncludedDate(a: DayPlanModule | undefined, b: DayPlanModule | undefined, isAsc = true): number {
    if (!a) { return isAsc ? -1 : 1 }
    if (!b) { return isAsc ? 1 : -1 }
    return compareByDate(a.includedDate.toDate(), b.includedDate.toDate(), GRANULARITY_SECOND, isAsc)
  }

  static compareByExpectedStudyDate(a: DayPlanModule | undefined, b: DayPlanModule | undefined, isAsc = true): number {
    if (!a) { return isAsc ? -1 : 1 }
    if (!b) { return isAsc ? 1 : -1 }
    return compareByDate(a.expectedStudyDate.toDate(), b.expectedStudyDate.toDate(), GRANULARITY_SECOND, isAsc)
  }

  static compareByLastStudyDate(a: DayPlanModule | undefined, b: DayPlanModule | undefined, isAsc = true): number {
    if (!a?.lastStudyDate) { return isAsc ? -1 : 1 }
    if (!b?.lastStudyDate) { return isAsc ? 1 : -1 }
    return compareByDate(a.lastStudyDate.toDate(), b.lastStudyDate.toDate(), GRANULARITY_SECOND, isAsc)
  }

  static compareByModuleId(a: DayPlanModule | undefined, b: DayPlanModule | undefined, isAsc = true): number {
    if (!a?.moduleId) { return isAsc ? -1 : 1 }
    if (!b?.moduleId) { return isAsc ? 1 : -1 }
    return localeCompare(a.moduleId, b.moduleId, isAsc)
  }

  /**
   * Compares two DayPlanModule objects.
   * @param a The first DayPlanModule object.
   * @param b The second DayPlanModule object.
   * @returns A negative value if 'a' is less than 'b', a positive value if 'a' is greater than 'b', or 0 if they are equal.
   */
  static compare(a: DayPlanModule | undefined, b: DayPlanModule | undefined): number {
    return DayPlanModule.compareByExpectedStudyDate(a, b, true)
    || DayPlanModule.compareByIncludedDate(a, b, false)
    //|| DayPlanModule.compareByLastStudyDate(a, b, false)
    //|| DayPlanModule.compareByModuleId(a, b, true)
  }

  // Sort and partition the modules into unfinished and finished modules
  static sortAndPartitionModules(modules: DayPlanModule[]): DayPlanModule[] {
    const p = partition(modules, m => !m.isFinished)
    const q = p.map(ms => ms.sort(DayPlanModule.compare))
    const r = q.flatMap(m => m)
    return r
  }

  /**
   * Checks if this DayPlanModule object is newer than another DayPlanModule object.
   * @param other The other DayPlanModule object to compare with.
   * @returns True if this object is newer than the provided object, false otherwise.
   */
  isNewer(other: DayPlanModule | undefined): boolean {
    return DayPlanModule.isNewer(this, other)
  }

  /**
   * Checks if this DayPlanModule object represents the first study.
   * @returns True if this object represents the first study, false otherwise.
   */
  isFirstStudy(): boolean {
    return this.actualStudyTime == 0 && (!this.lastStudyDate || compareByDate(this.includedDate.toDate(), this.lastStudyDate.toDate()) > 0)
  }

  get revisedCompletionTime(): number {
    return Math.min(this.estimatedCompletionTime, (this.acquisitionTarget - this.acquisitionProgress + this.reviewTarget - this.reviewProgress) * Constants.ESTIMATED_TIME_PER_CARD)
  }
}
